import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Trans } from '@lingui/react/macro';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base styles with enhanced interactions
  'inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-500 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=\'size-\'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:ring-offset-2 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive relative overflow-hidden group',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/80 hover:shadow-xs active:translate-y-0 active:shadow-xs focus-visible:border-ring before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:-translate-x-full before:transition-transform before:duration-500',
        destructive:
          'bg-red-500 text-white shadow-xs hover:bg-red-500/80 hover:shadow-xs active:translate-y-0 active:shadow-xs focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:-translate-x-full before:transition-transform before:duration-500',
        outline:
          'border border-black border-2 bg-background font-normal hover:bg-accent hover:text-accent-foreground hover:shadow-xs active:translate-y-0 active:shadow-xs dark:bg-input/30 dark:border-input dark:hover:bg-input/50 focus-visible:border-ring',
        secondary:
          'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 hover:shadow-xs active:translate-y-0 active:shadow-xs before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:-translate-x-full before:transition-transform before:duration-500',
        ghost:
          'text-slate-500 hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 hover:shadow-xs active:translate-y-0',
        link: 'text-primary underline-offset-4 hover:underline transition-colors duration-500',
        underline:
          'text-slate-800 font-normal max-w-[220px] underline hover:no-underline hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50 hover:shadow-xs active:translate-y-0',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3 rounded-lg',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 text-md rounded-lg px-6 has-[>svg]:px-4',
        icon: 'size-9 rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

// Enhanced loading state component
const LoadingSpinner = ({ className = '' }: { className?: string }) => (
  <svg className={cn('animate-spin', className)} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingText,
  children,
  disabled,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <LoadingSpinner className="size-4" />
          {loadingText || children}
        </>
      ) : (
        children
      )}
    </Comp>
  );
}

const ViewDetailsButton = ({ children, ...props }: ButtonProps) => (
  <Button variant="link" {...props}>
    {children ?? <Trans>View Details</Trans>}
  </Button>
);

export { Button, buttonVariants, ViewDetailsButton };
